/**
 * ===========================================================================
 * INSIGHTS → ESCALATION. The eight routes that had no client.
 * ===========================================================================
 *
 * Founder ruling 5 of 2026-09-07, ESCALATE half. The server side of this has
 * been complete since migration `1854000000000`: a four-rung ladder, a
 * resolver that is structurally unable to pick among candidates, a CHECK on
 * the outcome counts, and eight registered routes. Lane OPS-A measured the
 * other half and found **zero** — no function in this package reached any of
 * them, and no admin screen named one. Ops Console escalated every evening at
 * 20:00 with three lowercase substrings and a spreadsheet; Phibian had the
 * better mechanism and no door.
 *
 * Function names and paths mirror `findings/escalation/routes.ts` verbatim, so
 * that the route inventory and this file can be diffed by eye.
 *
 * ---------------------------------------------------------------------------
 * THE THREE ANSWERS ARE THE POINT, AND THEY ARE NOT ERRORS
 * ---------------------------------------------------------------------------
 * `POST .../escalate` answers **201 for every outcome**, including the ones
 * that routed to nobody. That is deliberate on the server and it must survive
 * the trip through here: an escalation that resolved nobody still HAPPENED,
 * and is now a row somebody has to act on. So this module never turns a
 * non-routed outcome into a thrown error, and never collapses the four:
 *
 *   routed        exactly one authorized target; it went there.
 *   ambiguous     more than one equally valid target. Surfaced, never picked.
 *   unconfigured  no rung named anybody. A configuration ABSENCE.
 *   unauthorized  somebody IS named and may not act. A REFUSAL, not an absence.
 *
 * The last two are the pair the lane contract names — absent versus
 * refused-for-scope — and an administrator reading "No escalation owner
 * configured" while three managers are configured would go and look in the
 * wrong screen. `ESCALATION_OUTCOME_MESSAGE` on the server states each
 * verbatim, and the server sends the sentence down as `message`; the surface
 * layer builds the fuller wording from the outcome, so a caller never has to
 * string-match to tell which happened.
 *
 * ---------------------------------------------------------------------------
 * `reachNarrowed` IS NOT DECORATION
 * ---------------------------------------------------------------------------
 * The open-escalation queue counts the ORGANIZATION's open escalations and
 * then removes the rows whose findings this caller does not reach. The server
 * says so rather than implying the count is the caller's — "scope applied
 * after pagination", named as a fact instead of shipped as a bug. A surface
 * that renders `total` beside `items.length` without saying which is which
 * would re-introduce exactly the defect the flag exists to disclose.
 */
import { get, post } from './transport.js';
const id = (value) => encodeURIComponent(value);
// ---------------------------------------------------------------------------
// 1 · Who WOULD this escalate to
// ---------------------------------------------------------------------------
/**
 * `GET /api/insights/findings/:id/escalation-preview` — needs `insights:read`
 * + `insights:triage` + `directory:read`.
 *
 * Writes nothing. 404 for a finding this caller does not reach, which is the
 * same answer as one that does not exist — deliberately, and for the reason
 * `finding-reach.ts` exists.
 */
export async function preview(findingId, options) {
    const body = await get(`/api/insights/findings/${id(findingId)}/escalation-preview`, options);
    return body.preview;
}
// ---------------------------------------------------------------------------
// 2 · Escalate
// ---------------------------------------------------------------------------
/**
 * `POST /api/insights/findings/:id/escalate` — needs `insights:read` +
 * `insights:triage` + `directory:read`.
 *
 * Returns 201 for ALL FOUR outcomes. Do not read a non-routed answer as a
 * failure: the row exists, and the three non-routed ones are the states a
 * manager now owns.
 */
export async function escalate(findingId, input = {}, options) {
    const body = await post(`/api/insights/findings/${id(findingId)}/escalate`, { agentRunId: input.agentRunId ?? null }, options);
    return body.escalation;
}
// ---------------------------------------------------------------------------
// 3 · The history for one finding
// ---------------------------------------------------------------------------
/**
 * `GET /api/insights/findings/:id/escalations` — needs `insights:read` +
 * `directory:read`. A READ: it does not charge the triage key, because looking
 * at where something went is reading.
 */
export async function forFinding(findingId, options) {
    const body = await get(`/api/insights/findings/${id(findingId)}/escalations`, options);
    return body.escalations;
}
// ---------------------------------------------------------------------------
// 4 · The management queue
// ---------------------------------------------------------------------------
/**
 * `GET /api/insights/escalations/open` — needs `insights:read` +
 * `directory:read`.
 *
 * Defaults on the server to the three non-routed outcomes. Passing `outcome`
 * narrows it, and the three must stay separately requestable: "nobody is
 * configured" and "the people you named cannot act" have different remedies,
 * and a queue that merged them would send an administrator to the wrong
 * screen.
 */
export async function open(query = {}, options) {
    // Built by hand: `outcome` is repeatable and `URLSearchParams` over a record
    // cannot express `outcome=ambiguous&outcome=unconfigured`.
    const parts = [
        ...(query.outcome ?? []).map((value) => `outcome=${encodeURIComponent(value)}`),
        query.limit === undefined ? '' : `limit=${query.limit}`,
        query.offset === undefined ? '' : `offset=${query.offset}`,
    ].filter((part) => part !== '');
    const suffix = parts.length > 0 ? `?${parts.join('&')}` : '';
    return get(`/api/insights/escalations/open${suffix}`, options);
}
// ---------------------------------------------------------------------------
// 5 · The human decision that closes an ambiguity
// ---------------------------------------------------------------------------
/**
 * `POST /api/insights/escalations/:id/decide` — needs `insights:read` +
 * `insights:triage` + `directory:read`.
 *
 * The chosen target MUST be one of the candidates the ladder recorded. A free
 * choice arrives as 409 `NotACandidateError`, not 400: the request is well
 * formed and the caller is authorized, and what is wrong is that a decision
 * may only pick among the targets organizational authority produced. A surface
 * therefore offers the recorded candidates and never a directory picker.
 */
export async function decide(escalationId, targetUserId, options) {
    const body = await post(`/api/insights/escalations/${id(escalationId)}/decide`, { targetUserId }, options);
    return body.escalation;
}
// ---------------------------------------------------------------------------
// 6–8 · Configuration — rungs 1 and 4 of the ladder
// ---------------------------------------------------------------------------
/**
 * `GET /api/insights/escalation-targets` — needs `org:manage`.
 *
 * This is where GH's `ESCALATION_NAMES = ['lacey','azak','abdirahman']` has an
 * exact structural home, and until now no customer could reach it.
 */
export async function targets(query = {}, options) {
    const suffix = query.includeRetired ? '?includeRetired=true' : '';
    const body = await get(`/api/insights/escalation-targets${suffix}`, options);
    return body.configs;
}
/**
 * `POST /api/insights/escalation-targets` — needs `org:manage`. Returns the id.
 *
 * The shape rules live in CHECK constraints and the route turns 23514/23505
 * into a 400: a fallback that names a person, a policy row with no policy key,
 * two live configurations for one policy and scope. This client does not keep
 * a second copy of those rules — a second copy is a second answer, and the
 * database's is the one that binds.
 */
export async function createTarget(input, options) {
    const body = await post('/api/insights/escalation-targets', {
        level: input.level,
        policyKind: input.policyKind ?? null,
        policyKey: input.policyKey ?? null,
        scopeType: input.scopeType ?? 'org',
        scopeProgramId: input.scopeProgramId ?? null,
        scopeLocationId: input.scopeLocationId ?? null,
        targetUserId: input.targetUserId ?? null,
        targetRoleKey: input.targetRoleKey ?? null,
    }, options);
    return body.id;
}
/**
 * `POST /api/insights/escalation-targets/:id/retire` — needs `org:manage`.
 *
 * RETIRE, NEVER DELETE. An escalation records the `configId` that chose its
 * target; removing the row would make an already-routed escalation
 * unexplainable, which is the failure the relationship snapshot exists to
 * prevent one level down. 204 on success, 404 for a row that is not there.
 */
export async function retireTarget(configId, options) {
    await post(`/api/insights/escalation-targets/${id(configId)}/retire`, {}, options);
}
//# sourceMappingURL=insightsEscalation.js.map